import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeftRight,
  Copy,
  Download,
  Upload,
  FileText,
  Loader2,
  CheckCircle2,
  X,
  Languages,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGUAGES = [
  { code: "", name: "Détection automatique" },
  { code: "FR", name: "Français" },
  { code: "EN-US", name: "Anglais (US)" },
  { code: "EN-GB", name: "Anglais (UK)" },
  { code: "DE", name: "Allemand" },
  { code: "ES", name: "Espagnol" },
  { code: "IT", name: "Italien" },
  { code: "PT-BR", name: "Portugais (Brésil)" },
  { code: "PT-PT", name: "Portugais (Portugal)" },
  { code: "NL", name: "Néerlandais" },
  { code: "PL", name: "Polonais" },
  { code: "RU", name: "Russe" },
  { code: "ZH", name: "Chinois" },
  { code: "JA", name: "Japonais" },
];

const TARGET_LANGUAGES = LANGUAGES.filter((l) => l.code !== "");

export default function Translation({ user, cabinet, onLogout }) {
  const [activeTab, setActiveTab] = useState("text");

  // Text translation state
  const [sourceText, setSourceText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [sourceLang, setSourceLang] = useState("");
  const [targetLang, setTargetLang] = useState("FR");
  const [translating, setTranslating] = useState(false);
  const [detectedLang, setDetectedLang] = useState(null);
  const [copied, setCopied] = useState(false);

  // Document translation state
  const [docFile, setDocFile] = useState(null);
  const [docTargetLang, setDocTargetLang] = useState("FR");
  const [docTranslating, setDocTranslating] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadFilename, setDownloadFilename] = useState("");

  // DeepL quota
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    const fetchQuota = async () => {
      try {
        const token = localStorage.getItem("lawliet_token");
        const res = await axios.get(`${API}/translation/quota`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setQuota(res.data);
      } catch {
        // silently fail
      }
    };
    fetchQuota();
  }, []);

  const handleTextTranslate = async () => {
    if (!sourceText.trim()) {
      toast.error("Veuillez entrer un texte à traduire");
      return;
    }
    setTranslating(true);
    setTranslatedText("");
    setDetectedLang(null);
    try {
      const token = localStorage.getItem("lawliet_token");
      const response = await axios.post(
        `${API}/translation/text`,
        { text: sourceText, target_lang: targetLang, source_lang: sourceLang || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTranslatedText(response.data.translated_text);
      if (response.data.detected_source_lang) {
        setDetectedLang(response.data.detected_source_lang);
      }
      toast.success("Traduction effectuée");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la traduction");
    } finally {
      setTranslating(false);
    }
  };

  const handleSwap = () => {
    if (!translatedText) return;
    const prevSource = sourceText;
    setSourceText(translatedText);
    setTranslatedText(prevSource);
    const prevSourceLang = sourceLang;
    setSourceLang(targetLang.split("-")[0]);
    setTargetLang(prevSourceLang || detectedLang || "FR");
    setDetectedLang(null);
  };

  const handleCopy = async () => {
    if (!translatedText) return;
    await navigator.clipboard.writeText(translatedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDocTranslate = async () => {
    if (!docFile) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }
    setDocTranslating(true);
    setDownloadUrl(null);
    try {
      const token = localStorage.getItem("lawliet_token");
      const formData = new FormData();
      formData.append("file", docFile);
      formData.append("target_lang", docTargetLang);

      const response = await axios.post(`${API}/translation/document`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 120000,
      });

      setDownloadUrl(`${BACKEND_URL}${response.data.download_url}`);
      setDownloadFilename(response.data.filename);
      toast.success("Document traduit avec succès !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la traduction du document");
    } finally {
      setDocTranslating(false);
    }
  };

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) setDocFile(file);
  }, []);

  const charCount = sourceText.length;
  const charLimit = 50000;

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="space-y-6" data-testid="translation-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-blue-900 flex items-center gap-3" style={{ fontFamily: "EB Garamond, serif" }}>
              <Languages className="h-8 w-8" />
              Traduction Juridique
            </h1>
            <p className="text-slate-600 mt-1">
              Traduction professionnelle propulsée par DeepL — textes et documents
            </p>
          </div>
          <Badge variant="outline" className="text-blue-800 border-blue-300 bg-blue-50">
            Powered by DeepL
          </Badge>
        </div>

        {/* DeepL Quota Banner */}
        {quota && (
          <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border text-sm ${
            quota.percent_used >= 90
              ? 'bg-red-50 border-red-200 text-red-800'
              : quota.percent_used >= 70
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-green-50 border-green-200 text-green-800'
          }`} data-testid="deepl-quota-banner">
            <Languages className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">Quota DeepL :</span>
            <span>
              {quota.characters_used.toLocaleString('fr-FR')} / {quota.characters_limit.toLocaleString('fr-FR')} caractères utilisés
            </span>
            <span className="font-semibold">({quota.percent_used}%)</span>
            {quota.percent_used >= 90 && (
              <span className="ml-auto font-semibold">Quota presque épuisé !</span>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab("text")}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === "text"
                ? "bg-white text-blue-900 shadow-sm"
                : "text-slate-600 hover:text-slate-800"
            }`}
            data-testid="tab-text"
          >
            Texte
          </button>
          <button
            onClick={() => setActiveTab("document")}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === "document"
                ? "bg-white text-blue-900 shadow-sm"
                : "text-slate-600 hover:text-slate-800"
            }`}
            data-testid="tab-document"
          >
            Document
          </button>
        </div>

        {/* === TEXT TRANSLATION === */}
        {activeTab === "text" && (
          <div className="space-y-4">
            {/* Language selector bar */}
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-slate-500 mb-1">Langue source</label>
                    <select
                      value={sourceLang}
                      onChange={(e) => setSourceLang(e.target.value)}
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="source-lang-select"
                    >
                      {LANGUAGES.map((l) => (
                        <option key={l.code} value={l.code}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                    {detectedLang && (
                      <p className="text-xs text-slate-500 mt-1">
                        Langue détectée : <span className="font-medium text-blue-700">{detectedLang}</span>
                      </p>
                    )}
                  </div>

                  <button
                    onClick={handleSwap}
                    disabled={!translatedText}
                    className="flex-shrink-0 p-2 rounded-full hover:bg-slate-100 disabled:opacity-30 transition"
                    title="Inverser les langues"
                    data-testid="swap-languages-btn"
                  >
                    <ArrowLeftRight className="h-5 w-5 text-slate-600" />
                  </button>

                  <div className="flex-1">
                    <label className="block text-xs font-medium text-slate-500 mb-1">Langue cible</label>
                    <select
                      value={targetLang}
                      onChange={(e) => setTargetLang(e.target.value)}
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="target-lang-select"
                    >
                      {TARGET_LANGUAGES.map((l) => (
                        <option key={l.code} value={l.code}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Text areas */}
            <div className="grid grid-cols-2 gap-4">
              {/* Source */}
              <Card className="relative">
                <CardContent className="pt-4">
                  <textarea
                    value={sourceText}
                    onChange={(e) => setSourceText(e.target.value)}
                    placeholder="Écrivez ou collez votre texte ici..."
                    className="w-full h-64 text-base text-slate-800 bg-transparent border-none focus:outline-none resize-none leading-relaxed"
                    data-testid="source-text-input"
                    maxLength={charLimit}
                  />
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                    <span className={`text-xs ${charCount > charLimit * 0.9 ? "text-amber-600" : "text-slate-400"}`}>
                      {charCount.toLocaleString()} / {charLimit.toLocaleString()}
                    </span>
                    {sourceText && (
                      <button
                        onClick={() => { setSourceText(""); setTranslatedText(""); setDetectedLang(null); }}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Target */}
              <Card className="relative bg-slate-50">
                <CardContent className="pt-4">
                  {translating ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Traduction en cours...</p>
                      </div>
                    </div>
                  ) : (
                    <textarea
                      value={translatedText}
                      readOnly
                      placeholder="La traduction apparaîtra ici..."
                      className="w-full h-64 text-base text-slate-800 bg-transparent border-none focus:outline-none resize-none leading-relaxed"
                      data-testid="translated-text-output"
                    />
                  )}
                  {translatedText && !translating && (
                    <div className="flex items-center justify-end mt-2 pt-2 border-t border-slate-100 gap-2">
                      <button
                        onClick={handleCopy}
                        className="flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 transition"
                        data-testid="copy-translation-btn"
                      >
                        {copied ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                            <span className="text-green-500">Copié !</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            Copier
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Translate button */}
            <div className="flex justify-center">
              <Button
                onClick={handleTextTranslate}
                disabled={translating || !sourceText.trim()}
                className="bg-blue-600 hover:bg-blue-700 px-10 py-3 text-base"
                data-testid="translate-text-btn"
              >
                {translating ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Traduction...
                  </>
                ) : (
                  <>
                    <Languages className="h-5 w-5 mr-2" />
                    Traduire
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* === DOCUMENT TRANSLATION === */}
        {activeTab === "document" && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Upload zone */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Document à traduire</CardTitle>
                  <CardDescription>PDF, DOCX, PPTX, XLSX, TXT (max 20 Mo)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div
                    onDrop={handleFileDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                    onClick={() => document.getElementById("doc-upload").click()}
                    data-testid="doc-drop-zone"
                  >
                    <input
                      id="doc-upload"
                      type="file"
                      accept=".pdf,.docx,.doc,.pptx,.xlsx,.txt"
                      className="hidden"
                      onChange={(e) => {
                        setDocFile(e.target.files[0]);
                        setDownloadUrl(null);
                      }}
                    />
                    {docFile ? (
                      <div className="space-y-3">
                        <FileText className="h-12 w-12 text-blue-600 mx-auto" />
                        <p className="font-medium text-slate-800">{docFile.name}</p>
                        <p className="text-sm text-slate-500">
                          {(docFile.size / 1024 / 1024).toFixed(2)} Mo
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDocFile(null);
                            setDownloadUrl(null);
                          }}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Supprimer
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Upload className="h-12 w-12 text-slate-300 mx-auto" />
                        <p className="text-slate-600 font-medium">
                          Glissez-déposez votre document ici
                        </p>
                        <p className="text-sm text-slate-400">ou cliquez pour sélectionner</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Options */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Options de traduction</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Langue cible
                    </label>
                    <select
                      value={docTargetLang}
                      onChange={(e) => setDocTargetLang(e.target.value)}
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="doc-target-lang-select"
                    >
                      {TARGET_LANGUAGES.map((l) => (
                        <option key={l.code} value={l.code}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800 space-y-1">
                    <p className="font-semibold">Formats supportés :</p>
                    <p>PDF, Word (.docx), PowerPoint (.pptx), Excel (.xlsx), Texte (.txt)</p>
                    <p className="text-xs text-blue-600 mt-2">
                      La mise en forme originale est préservée lors de la traduction.
                    </p>
                  </div>

                  {downloadUrl && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="font-medium">Document traduit !</span>
                      </div>
                      <a
                        href={downloadUrl}
                        download={downloadFilename}
                        className="flex items-center gap-2 bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 transition text-sm w-full justify-center"
                        data-testid="download-translated-doc-btn"
                      >
                        <Download className="h-4 w-4" />
                        Télécharger le document traduit
                      </a>
                    </div>
                  )}

                  <Button
                    onClick={handleDocTranslate}
                    disabled={!docFile || docTranslating}
                    className="w-full bg-blue-600 hover:bg-blue-700 py-3"
                    data-testid="translate-doc-btn"
                  >
                    {docTranslating ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Traduction en cours... (peut prendre 1-2 min)
                      </>
                    ) : (
                      <>
                        <Languages className="h-5 w-5 mr-2" />
                        Traduire le document
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
